import {
    Box,
    Collapse,
    Flex,
    FormControl,
    FormErrorMessage,
    HStack,
    Input,
    Select,
    Text,
    useToast,
} from '@chakra-ui/react';
import {
    InventoryBin,
    InventoryTag,
} from '../../types/inventory';
import {
    INVENTORY_CATEGORY_OPTIONS,
    INVENTORY_PART_TYPE_OPTIONS,
    parseInventoryPartType,
} from '../../utils/inventoryConstants';
import { inventoryFieldProps } from '../../utils/inventoryFieldStyles';
import UnitSelectField from './UnitSelectField';
import CurrencyInput from './CurrencyInput';
import InventoryTypeaheadField, { TypeaheadOption } from './InventoryTypeaheadField';
import InventorySupplierDropdown from './InventorySupplierDropdown';
import QuantityInputField from './QuantityInputField';
import QuantityComboField from './QuantityComboField';
import InventoryItemImageField from './InventoryItemImageField';
import InventoryTagsField from './InventoryTagsField';
import InventoryBinsField from './InventoryBinsField';
import RoNumberField from './RoNumberField';
import InventoryAdditionalPartNumbersField from './InventoryAdditionalPartNumbersField';
import {
    FieldLabel,
    InventoryItemFormErrors,
    InventoryItemFormState,
    RestockPillTabs,
    SectionHead,
} from './inventoryItemFormShared';

export interface InventoryItemFormBodyProps {
    form: InventoryItemFormState;
    fieldErrors: InventoryItemFormErrors;
    supplierDisplay: string;
    supplierOptions: TypeaheadOption[];
    isSupplierLoading?: boolean;
    onSupplierSearch: (value: string) => void;
    onSupplierChange: (displayOrId: string) => void;
    locationDisplay: string;
    locationOptions: TypeaheadOption[];
    onLocationChange: (displayOrId: string) => void;
    bins: Array<InventoryBin>;
    onBinsChange: (bins: Array<InventoryBin>) => void;
    tags: Array<InventoryTag>;
    onTagsChange: (tags: Array<InventoryTag>) => void;
    imagePreviewUrl: string | null;
    imageKey: string | undefined;
    isImageUploading?: boolean;
    onImageFileSelected: (file: File) => Promise<void>;
    onImageClear: () => void;
    photoAndTagsOpen: boolean;
    onPhotoAndTagsOpenChange: (open: boolean) => void;
    detailsOpen: boolean;
    onDetailsOpenChange: (open: boolean) => void;
    updateField: <K extends keyof InventoryItemFormState>(
        key: K,
        value: InventoryItemFormState[K],
    ) => void;
    setForm: React.Dispatch<React.SetStateAction<InventoryItemFormState>>;
    /** When set, clamps in-stock quantity to 1..maxInStockQuantity. */
    maxInStockQuantity?: number;
    isUnitsLoading?: boolean;
    isUnitsError?: boolean;
    businessLineOptions?: Array<string>;
    showBusinessLine?: boolean;
    /** When true, unit cost is display-only (e.g. move-to-inventory from order buy price). */
    unitCostReadOnly?: boolean;
    /** Alternate part numbers (Add Item). Omitted when not used. */
    additionalPartNumbers?: Array<string>;
    onAdditionalPartNumbersChange?: (partNumbers: Array<string>) => void;
}

const CollapsibleSection = ({
    title,
    isOpen,
    onToggle,
    children,
}: {
    title: string;
    isOpen: boolean;
    onToggle: () => void;
    children: React.ReactNode;
}) => (
    <Box py={5} borderBottomWidth="1px" borderColor="gray.200" _last={{ borderBottomWidth: 0 }}>
        <Flex
            as="button"
            type="button"
            align="center"
            gap={2}
            cursor="pointer"
            onClick={onToggle}
            w="100%"
            textAlign="left"
        >
            <Text
                fontSize="sm"
                color="gray.500"
                w="14px"
                flexShrink={0}
                fontWeight="bold"
            >
                {isOpen ? '–' : '+'}
            </Text>
            <Text
                fontSize="sm"
                fontWeight="bold"
                letterSpacing="0.06em"
                textTransform="uppercase"
                color="gray.600"
            >
                {title}
            </Text>
        </Flex>
        <Collapse in={isOpen} animateOpacity>
            <Box pt={3.5}>{children}</Box>
        </Collapse>
    </Box>
);

const InventoryItemFormBody = ({
    form,
    fieldErrors,
    supplierDisplay,
    supplierOptions,
    isSupplierLoading,
    onSupplierSearch,
    onSupplierChange,
    locationDisplay,
    locationOptions,
    onLocationChange,
    bins,
    onBinsChange,
    tags,
    onTagsChange,
    imagePreviewUrl,
    isImageUploading,
    onImageFileSelected,
    onImageClear,
    photoAndTagsOpen,
    onPhotoAndTagsOpenChange,
    detailsOpen,
    onDetailsOpenChange,
    updateField,
    setForm,
    maxInStockQuantity,
    isUnitsLoading,
    isUnitsError,
    businessLineOptions = [],
    showBusinessLine = false,
    unitCostReadOnly = false,
    additionalPartNumbers,
    onAdditionalPartNumbersChange,
}: InventoryItemFormBodyProps) => {
    const toast = useToast();

    return (
        <>
            <Box py={5} borderBottomWidth="1px" borderColor="gray.200">
                <SectionHead title="Inventory Options" />
                <HStack spacing={3} align="flex-start">
                    <FormControl isRequired flex={1}>
                        <FieldLabel>Quantity to Inventory</FieldLabel>
                        {maxInStockQuantity != null ? (
                            <QuantityComboField
                                value={form.inStockQuantity}
                                max={maxInStockQuantity}
                                onChange={(val) => updateField('inStockQuantity', val)}
                            />
                        ) : (
                            <QuantityInputField
                                value={form.inStockQuantity}
                                onChange={(val) => updateField('inStockQuantity', val ?? 0)}
                            />
                        )}
                    </FormControl>
                    <Box flex={1}>
                        <FieldLabel>Bin</FieldLabel>
                        <InventoryBinsField
                            size="sm"
                            value={bins}
                            onChange={onBinsChange}
                            locationId={form.locationId || null}
                            label=""
                            isDisabled={!form.locationId}
                            placeholder={
                                form.locationId ? 'Search bins' : 'Select a location first'
                            }
                        />
                    </Box>
                </HStack>
            </Box>

            <Box py={5} borderBottomWidth="1px" borderColor="gray.200">
                <SectionHead
                    title="Cost And Restocking"
                    hint="Turn restocking off to skip low-stock alerts"
                />
                <HStack spacing={3} align="flex-start" mb={3}>
                    <FormControl flex={1}>
                        <FieldLabel>Unit Cost</FieldLabel>
                        <CurrencyInput
                            value={form.unitCost}
                            onChange={(val) => updateField('unitCost', val)}
                            useThousandsSeparator
                            isDisabled={unitCostReadOnly}
                        />
                    </FormControl>
                    <FormControl flex={1}>
                        <FieldLabel>Restock Alerts</FieldLabel>
                        <RestockPillTabs
                            value={form.restockAlert}
                            onChange={(on) => {
                                setForm((prev) => ({
                                    ...prev,
                                    restockAlert: on,
                                    ...(on
                                        ? {
                                              reorderQuantity: prev.reorderQuantity ?? 0,
                                              targetQuantity: prev.targetQuantity ?? 0,
                                          }
                                        : {}),
                                }));
                            }}
                        />
                    </FormControl>
                </HStack>
                {form.restockAlert && (
                    <HStack spacing={3} align="flex-start">
                        <FormControl isRequired={form.restockAlert} flex={1}>
                            <FieldLabel>Reorder At</FieldLabel>
                            <QuantityInputField
                                value={form.reorderQuantity}
                                onChange={(val) => updateField('reorderQuantity', val)}
                                allowEmpty={!form.restockAlert}
                            />
                        </FormControl>
                        <FormControl isRequired={form.restockAlert} flex={1}>
                            <FieldLabel>Restock To</FieldLabel>
                            <QuantityInputField
                                value={form.targetQuantity}
                                onChange={(val) => updateField('targetQuantity', val)}
                                allowEmpty={!form.restockAlert}
                            />
                        </FormControl>
                    </HStack>
                )}
            </Box>

            <CollapsibleSection
                title="Photo And Tags"
                isOpen={photoAndTagsOpen}
                onToggle={() => onPhotoAndTagsOpenChange(!photoAndTagsOpen)}
            >
                <InventoryItemImageField
                    previewUrl={imagePreviewUrl}
                    alt={form.name}
                    isUploading={!!isImageUploading}
                    cropTitle={`${form.name.trim() || '—'} · ${form.manufacturerPartNumber.trim() || '—'}`}
                    onFileSelected={async (file) => {
                        try {
                            await onImageFileSelected(file);
                        } catch {
                            toast({
                                title: 'Image upload failed',
                                status: 'error',
                                duration: 4000,
                                isClosable: true,
                            });
                        }
                    }}
                    onClear={onImageClear}
                />
                <Box mt={3}>
                    <InventoryTagsField value={tags} onChange={onTagsChange} />
                </Box>
            </CollapsibleSection>

            <CollapsibleSection
                title="Details"
                isOpen={detailsOpen}
                onToggle={() => onDetailsOpenChange(!detailsOpen)}
            >
                <HStack spacing={3} align="flex-start" mb={3}>
                    <FormControl isRequired w="126px" flexShrink={0}>
                        <FieldLabel>Category</FieldLabel>
                        <Select
                            size="sm"
                            value={form.category}
                            isDisabled
                            {...inventoryFieldProps}
                        >
                            {INVENTORY_CATEGORY_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                </option>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl isRequired flex={1}>
                        <FieldLabel>Mfg. Part Number</FieldLabel>
                        <Input
                            size="sm"
                            value={form.manufacturerPartNumber}
                            isDisabled
                            {...inventoryFieldProps}
                        />
                    </FormControl>
                </HStack>
                <FormControl isRequired mb={3}>
                    <FieldLabel>Name</FieldLabel>
                    <Input
                        size="sm"
                        value={form.name}
                        onChange={(e) => updateField('name', e.target.value)}
                        {...inventoryFieldProps}
                    />
                </FormControl>
                {additionalPartNumbers != null && onAdditionalPartNumbersChange && (
                    <Box mb={3}>
                        <InventoryAdditionalPartNumbersField
                            value={additionalPartNumbers}
                            onChange={onAdditionalPartNumbersChange}
                            primaryPartNumber={form.manufacturerPartNumber}
                            label="Additional Part Numbers"
                            labelProps={{
                                fontSize: 'sm',
                                fontWeight: 'semibold',
                                color: 'gray.600',
                                mb: 1.5,
                            }}
                        />
                    </Box>
                )}
                <HStack spacing={3} align="flex-start" mb={3}>
                    <FormControl isRequired flex={1}>
                        <FieldLabel>Part Type</FieldLabel>
                        <Select
                            size="sm"
                            value={form.partType}
                            onChange={(e) =>
                                updateField('partType', parseInventoryPartType(e.target.value))
                            }
                            {...inventoryFieldProps}
                        >
                            {INVENTORY_PART_TYPE_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                </option>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl isRequired flex={1} isInvalid={!!fieldErrors.supplierId}>
                        <FieldLabel>Supplier</FieldLabel>
                        <InventorySupplierDropdown
                            value={form.supplierId}
                            displayLabel={supplierDisplay}
                            options={supplierOptions}
                            onSearch={onSupplierSearch}
                            onSelect={(option) => onSupplierChange(option.value)}
                            isLoading={isSupplierLoading}
                        />
                        <FormErrorMessage>{fieldErrors.supplierId}</FormErrorMessage>
                    </FormControl>
                </HStack>
                <FormControl mb={3}>
                    <FieldLabel>
                        Origin
                        <Text
                            as="span"
                            color="gray.400"
                            fontWeight="medium"
                            fontSize="xs"
                            ml={1}
                        >
                            (Original RO item was ordered for)
                        </Text>
                    </FieldLabel>
                    <RoNumberField
                        value={form.origin}
                        onChange={(val) => updateField('origin', val)}
                        isDisabled
                        placeholderNode={
                            <Text as="span" fontSize="sm">
                                Origin RO #{' '}
                                <Text as="span" fontSize="xs">
                                    (optional)
                                </Text>
                            </Text>
                        }
                    />
                </FormControl>
                <HStack spacing={3} align="flex-start" mb={showBusinessLine ? 3 : 0}>
                    <FormControl isRequired flex={1} isInvalid={!!fieldErrors.locationId}>
                        <FieldLabel>Location</FieldLabel>
                        <InventoryTypeaheadField
                            value={locationDisplay}
                            onChange={onLocationChange}
                            options={locationOptions}
                            placeholder="Search locations"
                            isDisabled
                        />
                        <FormErrorMessage>{fieldErrors.locationId}</FormErrorMessage>
                    </FormControl>
                    <FormControl
                        isRequired
                        flex={1}
                        isInvalid={!!fieldErrors.unit || !!isUnitsError}
                    >
                        <FieldLabel>Unit</FieldLabel>
                        <UnitSelectField
                            value={form.unit}
                            onChange={(val) => updateField('unit', val)}
                            isDisabled
                        />
                        {fieldErrors.unit && (
                            <FormErrorMessage>{fieldErrors.unit}</FormErrorMessage>
                        )}
                    </FormControl>
                </HStack>
                {showBusinessLine && (
                    <FormControl>
                        <FieldLabel>Business Line</FieldLabel>
                        <Select
                            size="sm"
                            value={form.businessLine}
                            onChange={(e) => updateField('businessLine', e.target.value)}
                            {...inventoryFieldProps}
                        >
                            <option value="">Select business line</option>
                            {businessLineOptions.map((name) => (
                                <option key={name} value={name}>
                                    {name}
                                </option>
                            ))}
                        </Select>
                    </FormControl>
                )}
            </CollapsibleSection>
        </>
    );
};

export default InventoryItemFormBody;
